import { memo, useEffect, useRef } from 'react'
import { useTheme } from '@mui/material'
import tinycolor from 'tinycolor2'
import {
    CrosshairMode,
    ISeriesApi,
    LineStyle,
    MismatchDirection,
    MouseEventParams,
    SeriesType,
    Time
} from 'lightweight-charts'
import Chart from '../chart'
import { callNative, isNativeEmbedded } from '../../../util/nativeBridge'

/**
 * Inline crosshair for the native (Android / iOS) embedded overview chart.
 *
 * - Long-press (lightweight-charts tracking mode) shows a dotted vertical line; the finger drags it
 *   and lifting the finger hides it (`trackingMode.exitMode = OnTouchEnd`, set by ChartWrapper).
 * - A floating OHLC card is centred on the line and every bar except the active one is dimmed.
 * - Two fingers enter "range" mode: a line on each finger's bar, `O` of the left bar and `C` of the
 *   right bar, and the difference between them in a tinted pill. Bars outside the range are dimmed.
 * - Reports `{ alertEdit, crosshair }` to native via `onOverviewInteraction` so the host can stop its
 *   sheet / list from intercepting the drag, and hides itself while an on-chart alert is being edited.
 *
 * Rendering is imperative DOM (no React state per move) — crosshair moves fire on every frame.
 */

type Props = {
    chart: Chart
}

type Bar = {
    time: Time
    open?: number
    high?: number
    low?: number
    close?: number
    value?: number
}

type Geometry = {
    /** Pane left edge / top edge, in overlay-root coordinates. */
    left: number
    top: number
    width: number
    height: number
    halfBar: number
}

type Palette = {
    text: string
    secondary: string
    up: string
    down: string
    dim: string
    cardBg: string
    cardBorder: string
    line: string
}

/** Window event that drops all inline-crosshair state; dispatched by `ChartManager.resetInlineCrosshair()`. */
export const INLINE_CROSSHAIR_RESET_EVENT = 't2t:inline-crosshair-reset'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec']
const pad2 = (n: number) => (n < 10 ? '0' : '') + n

/** Series times are already shifted into the chart timezone, so UTC getters print the chart's local time. */
function formatBarTime(time: Time, intraday: boolean): string {
    if (typeof time === 'object' && time && 'year' in time) {
        return `${time.day} ${MONTHS[(time.month || 1) - 1]}`
    }
    if (typeof time === 'number') {
        const dt = new Date(time * 1000)
        const date = `${dt.getUTCDate()} ${MONTHS[dt.getUTCMonth()]}`
        return intraday ? `${date}, ${pad2(dt.getUTCHours())}:${pad2(dt.getUTCMinutes())}` : `${date} ${dt.getUTCFullYear()}`
    }
    if (typeof time === 'string') return time
    return ''
}

function isIntraday(time: Time, prevTime: Time | null | undefined): boolean {
    if (typeof time === 'number' && typeof prevTime === 'number') return Math.abs(time - prevTime) < 86400
    if (typeof time === 'number') {
        const dt = new Date(time * 1000)
        return dt.getUTCHours() !== 0 || dt.getUTCMinutes() !== 0
    }
    return false
}

function isOhlc(bar: Bar): bar is Bar & { open: number; high: number; low: number; close: number } {
    return bar.open !== undefined && bar.close !== undefined
}

function makeDiv(parent: HTMLElement, css: string): HTMLDivElement {
    const d = document.createElement('div')
    d.style.cssText = `position:absolute;pointer-events:none;display:none;${css}`
    parent.appendChild(d)
    return d
}

const CARD_CSS =
    'top:6px;padding:3px 6px;border-radius:6px;white-space:nowrap;text-align:center;box-sizing:border-box;' +
    "font:500 12px/1.3 'Work Sans',-apple-system,Roboto,Helvetica,Arial,sans-serif;font-variant-numeric:tabular-nums;"
/** 1px vertical line, 6px dash / 6px gap — mirrors lightweight-charts `LineStyle.LargeDashed`. Colour via `--line`. */
const LINE_CSS =
    'top:0;width:1px;background:repeating-linear-gradient(to bottom,var(--line) 0 6px,transparent 6px 12px);'

/** Replays a `touchend` for `touches` so lightweight-charts resets its single-touch bookkeeping. */
function replayTouchEnd(target: EventTarget, touches: TouchList | undefined) {
    try {
        const changed = Array.prototype.slice.call(touches ?? []) as Touch[]
        if (!changed.length || typeof TouchEvent !== 'function') return
        target.dispatchEvent(
            new TouchEvent('touchend', { bubbles: true, cancelable: true, touches: [], targetTouches: [], changedTouches: changed })
        )
    } catch {
        // TouchEvent constructor unavailable — nothing to reset.
    }
}

const InlineCrosshairOverlay = ({ chart }: Props) => {
    const theme = useTheme()
    const rootRef = useRef<HTMLDivElement>(null)
    const paletteRef = useRef<Palette | null>(null)

    paletteRef.current = {
        text: theme.palette.text.primary,
        secondary: theme.palette.text.secondary,
        up: theme.palette.success.main,
        down: theme.palette.error.main,
        dim: tinycolor(theme.palette.background.paper).setAlpha(0.55).toRgbString(),
        cardBg: tinycolor(theme.palette.background.paper).setAlpha(0.96).toRgbString(),
        cardBorder: `1px solid ${theme.palette.divider}`,
        line: theme.palette.text.secondary
    }

    useEffect(() => {
        const root = rootRef.current
        const api = chart?.chartApi
        if (!root || !api) return

        const dimL = makeDiv(root, 'top:0;')
        const dimR = makeDiv(root, 'top:0;')
        const card = makeDiv(root, CARD_CSS)
        const lineL = makeDiv(root, LINE_CSS)
        const lineR = makeDiv(root, LINE_CSS)
        const cardL = makeDiv(root, CARD_CSS)
        const cardR = makeDiv(root, CARD_CSS)
        const cardM = makeDiv(root, CARD_CSS)
        const all = [dimL, dimR, card, lineL, lineR, cardL, cardR, cardM]

        const native = isNativeEmbedded()
        let crosshairActive = false
        let alertEdit = false
        let lastReport = ''
        /**
         * Touch ownership. `idle`: lightweight-charts owns the touch (its long-press tracking mode
         * drives the single crosshair). `range`: two fingers, we render the range. `single`: one
         * finger left after a range gesture — we keep the touch and drive the library's crosshair
         * ourselves (`setCrosshairPosition`) so the single card follows the remaining finger without
         * requiring a fresh long-press.
         */
        const range = { mode: 'idle' as 'idle' | 'range' | 'single', firstTarget: null as EventTarget | null }
        const owningTouch = () => range.mode !== 'idle'
        /** True while we dispatch a synthetic `touchend`; our own touch listeners must ignore it. */
        let replaying = false
        const resetLibraryTouch = (target: EventTarget, touches: TouchList | undefined) => {
            replaying = true
            try {
                replayTouchEnd(target, touches)
            } finally {
                replaying = false
            }
        }

        const report = () => {
            if (!native) return
            const next = JSON.stringify({ alertEdit, crosshair: crosshairActive })
            if (next === lastReport) return
            lastReport = next
            callNative('onOverviewInteraction', next)
        }
        const setCrosshairActive = (on: boolean) => {
            if (crosshairActive === on) return
            crosshairActive = on
            report()
        }

        const hideAll = () => all.forEach((d) => (d.style.display = 'none'))

        const primarySeries = (): ISeriesApi<SeriesType> | undefined => chart.primarySeriesGroup?.getPrimarySeriesApi()

        const palette = (series?: ISeriesApi<SeriesType>): Palette => {
            const p = paletteRef.current!
            const so = (series?.options() ?? {}) as { upColor?: string; downColor?: string }
            return { ...p, up: so.upColor ?? p.up, down: so.downColor ?? p.down }
        }

        const geometry = (): Geometry | null => {
            const el = api.chartElement()
            if (!el) return null
            const rootRect = root.getBoundingClientRect()
            const rect = el.getBoundingClientRect()
            const ts = api.timeScale()
            let leftW = 0
            try {
                leftW = api.priceScale('left').width() || 0
            } catch {
                leftW = 0
            }
            const width = ts.width() || Math.max(0, rect.width - leftW)
            const height = Math.max(0, rect.height - (ts.height() || 0))
            const barSpacing = ts.options().barSpacing || 6
            return {
                left: rect.left - rootRect.left + leftW,
                top: rect.top - rootRect.top,
                width,
                height,
                halfBar: Math.max(2, barSpacing / 2)
            }
        }

        const fmtPrice = (series: ISeriesApi<SeriesType> | undefined, v: number) => {
            try {
                if (series) return series.priceFormatter().format(v)
            } catch {
                // fall through
            }
            return v.toFixed(2)
        }

        const styleCard = (el: HTMLDivElement, p: Palette) => {
            el.style.display = 'block'
            el.style.color = p.text
            el.style.background = p.cardBg
            el.style.border = p.cardBorder
            el.style.top = `${g().top + 6}px`
        }
        let currentGeometry: Geometry | null = null
        const g = () => currentGeometry!

        /** Centre `el` on `cx`, clamped inside the pane. Returns its horizontal extent. */
        const placeCard = (el: HTMLDivElement, cx: number) => {
            const geo = g()
            const w = el.offsetWidth
            let x = cx - w / 2
            const minX = geo.left + 4
            const maxX = geo.left + geo.width - w - 4
            if (x > maxX) x = maxX
            if (x < minX) x = minX
            el.style.left = `${Math.round(x)}px`
            return { left: x, right: x + w }
        }

        const applyDim = (p: Palette, fromX: number, toX: number) => {
            const geo = g()
            const leftEnd = Math.max(geo.left, Math.min(fromX, geo.left + geo.width))
            const rightStart = Math.min(geo.left + geo.width, Math.max(toX, geo.left))
            for (const [el, left, width] of [
                [dimL, geo.left, leftEnd - geo.left],
                [dimR, rightStart, geo.left + geo.width - rightStart]
            ] as [HTMLDivElement, number, number][]) {
                el.style.display = 'block'
                el.style.left = `${left}px`
                el.style.width = `${Math.max(0, width)}px`
                el.style.top = `${geo.top}px`
                el.style.height = `${geo.height}px`
                el.style.background = p.dim
            }
        }

        const timeRow = (p: Palette, text: string) =>
            text ? `<div style="color:${p.secondary};font-weight:500;font-size:11px">${text}</div>` : ''
        const label = (p: Palette, k: string) => `<span style="color:${p.text};margin-right:4px">${k}</span>`
        const value = (color: string, v: string) => `<span style="color:${color}">${v}</span>`

        // ── Single finger: OHLC card ─────────────────────────────────────────
        type SingleHit = { series: ISeriesApi<SeriesType>; bar: Bar; logical: number | null; x: number }

        /** Resolve the bar under a library crosshair event (long-press tracking mode). */
        const hitFromParam = (param: MouseEventParams<Time>): SingleHit | null => {
            if (!param.point || param.time === undefined) return null
            let series = primarySeries()
            let bar: Bar | undefined = series ? (param.seriesData.get(series) as Bar | undefined) : undefined
            if (!bar) {
                // Primary series not in this event — take the first series carrying price data.
                for (const [s, d] of param.seriesData) {
                    const b = d as Bar
                    if (b && (b.close !== undefined || b.value !== undefined)) {
                        series = s
                        bar = b
                        break
                    }
                }
            }
            if (!series || !bar) return null
            const x = api.timeScale().timeToCoordinate(param.time) ?? param.point.x
            return { series, bar, logical: typeof param.logical === 'number' ? param.logical : null, x }
        }

        const renderSingle = (hit: SingleHit | null) => {
            currentGeometry = geometry()
            if (!currentGeometry || !hit) {
                hideAll()
                return
            }
            const { series, bar } = hit
            const p = palette(series)
            const geo = g()
            const cx = geo.left + hit.x
            applyDim(p, cx - geo.halfBar, cx + geo.halfBar)

            let prev: Bar | null = null
            if (hit.logical !== null) {
                prev = (series.dataByIndex(hit.logical - 1, MismatchDirection.None) as Bar | null) ?? null
            }
            const ohlc = isOhlc(bar)
            const last = ohlc ? bar.close : (bar.value as number)
            const ref = prev ? (prev.close ?? prev.value ?? null) : ohlc ? bar.open : null
            const dir = ref !== null && ref !== undefined ? (last >= ref ? p.up : p.down) : ohlc && bar.close >= bar.open ? p.up : p.down
            let change = ''
            if (ref) {
                const abs = last - ref
                const sign = abs >= 0 ? '+' : ''
                change = `<div style="color:${dir}">${sign}${fmtPrice(series, abs)} ${sign}${((abs / ref) * 100).toFixed(2)}%</div>`
            }
            const grid = ohlc
                ? `<div style="display:grid;grid-template-columns:auto auto;column-gap:8px;text-align:left">` +
                  `<div>${label(p, 'O')}${value(dir, fmtPrice(series, bar.open))}</div>` +
                  `<div>${label(p, 'H')}${value(dir, fmtPrice(series, bar.high))}</div>` +
                  `<div>${label(p, 'L')}${value(dir, fmtPrice(series, bar.low))}</div>` +
                  `<div>${label(p, 'C')}${value(dir, fmtPrice(series, bar.close))}</div>` +
                  `</div>`
                : `<div>${value(dir, fmtPrice(series, last))}</div>`
            card.innerHTML = grid + change + timeRow(p, formatBarTime(bar.time, isIntraday(bar.time, prev?.time)))
            styleCard(card, p)
            placeCard(card, cx)
        }

        // ── Two fingers: range between bars ──────────────────────────────────
        const barAtX = (series: ISeriesApi<SeriesType>, x: number) => {
            const ts = api.timeScale()
            const logical = ts.coordinateToLogical(x)
            if (logical === null) return null
            const idx = Math.round(logical)
            const bar =
                (series.dataByIndex(idx, MismatchDirection.None) as Bar | null) ??
                (series.dataByIndex(idx, MismatchDirection.NearestLeft) as Bar | null) ??
                (series.dataByIndex(idx, MismatchDirection.NearestRight) as Bar | null)
            if (!bar) return null
            const bx = ts.timeToCoordinate(bar.time) ?? x
            const prev = series.dataByIndex(idx - 1, MismatchDirection.NearestLeft) as Bar | null
            return { bar, x: bx, logical: idx, prevTime: prev?.time ?? null }
        }

        const renderRange = (x1: number, x2: number): boolean => {
            currentGeometry = geometry()
            const series = primarySeries()
            if (!currentGeometry || !series) return false
            const geo = g()
            const a = barAtX(series, Math.min(x1, x2))
            const b = barAtX(series, Math.max(x1, x2))
            if (!a || !b) return false
            const p = palette(series)
            const ohlc = isOhlc(a.bar) && isOhlc(b.bar)
            const lx = geo.left + a.x
            const rx = geo.left + b.x

            applyDim(p, lx - geo.halfBar, rx + geo.halfBar)
            for (const [el, x] of [[lineL, lx], [lineR, rx]] as [HTMLDivElement, number][]) {
                el.style.display = 'block'
                el.style.left = `${Math.round(x)}px`
                el.style.top = `${geo.top}px`
                el.style.height = `${geo.height}px`
                el.style.setProperty('--line', p.line)
            }

            const startV = ohlc ? (a.bar.open as number) : (a.bar.value as number)
            const endV = ohlc ? (b.bar.close as number) : (b.bar.value as number)
            const barColor = (bar: Bar, v: number) =>
                ohlc ? ((bar.close as number) >= (bar.open as number) ? p.up : p.down) : v >= startV ? p.up : p.down
            const endCard = (el: HTMLDivElement, k: string, bar: Bar, v: number, prevTime: Time | null) => {
                el.innerHTML =
                    `<div>${ohlc ? label(p, k) : ''}${value(barColor(bar, v), fmtPrice(series, v))}</div>` +
                    timeRow(p, formatBarTime(bar.time, isIntraday(bar.time, prevTime)))
                styleCard(el, p)
                el.style.width = ''
            }
            endCard(cardL, 'O', a.bar, startV, a.prevTime)
            endCard(cardR, 'C', b.bar, endV, b.prevTime)

            const diff = endV - startV
            const pct = startV ? (diff / startV) * 100 : 0
            const sign = diff >= 0 ? '+' : ''
            const dir = diff >= 0 ? p.up : p.down
            cardM.innerHTML =
                `<div>${sign}${fmtPrice(series, diff)}</div>` +
                `<div style="font-weight:500;font-size:11px">${sign}${pct.toFixed(2)}%</div>`
            styleCard(cardM, p)
            cardM.style.color = dir
            cardM.style.background = tinycolor(dir).setAlpha(0.14).toRgbString()
            cardM.style.border = 'none'
            cardM.style.width = ''

            // One row: O centred on the left line, C on the right line, change card between them.
            // Enough room → the change card stretches to fill the gap between O and C.
            // Not enough → the change card keeps its natural width centred between the lines and
            // O / C are pushed outwards off their lines so nothing overlaps.
            const GAP = 6
            const wL = cardL.offsetWidth
            const wR = cardR.offsetWidth
            const wM = cardM.offsetWidth
            const minX = geo.left + 4
            const maxX = geo.left + geo.width - 4
            const mid = (lx + rx) / 2
            let oLeft = lx - wL / 2
            let cLeft = rx - wR / 2
            let mLeft: number
            let mWidth: number
            if (cLeft - (oLeft + wL) >= wM + 2 * GAP) {
                mLeft = oLeft + wL + GAP
                mWidth = cLeft - GAP - mLeft
            } else {
                mLeft = mid - wM / 2
                mWidth = wM
                oLeft = mLeft - GAP - wL
                cLeft = mLeft + mWidth + GAP
            }
            // Keep the row inside the pane; shift the whole group rather than collapsing it.
            const shift = Math.max(0, minX - oLeft) - Math.max(0, cLeft + wR - maxX)
            oLeft += shift
            mLeft += shift
            cLeft += shift
            cardL.style.left = `${Math.round(oLeft)}px`
            cardR.style.left = `${Math.round(cLeft)}px`
            cardM.style.left = `${Math.round(mLeft)}px`
            cardM.style.width = `${Math.round(mWidth)}px`
            return true
        }

        /** Drop every transient state: touch ownership, overlays, library crosshair, native flag. */
        const reset = () => {
            range.mode = 'idle'
            range.firstTarget = null
            hideAll()
            try {
                api.clearCrosshairPosition()
            } catch {
                // chart may be mid-update
            }
            setCrosshairActive(false)
        }
        const endRange = () => {
            if (owningTouch()) reset()
        }

        // ── Alert edit mode: crosshair off while an on-chart alert is staged / dragged ──
        const setAlertEdit = (on: boolean) => {
            if (alertEdit === on) return
            alertEdit = on
            if (on) {
                endRange()
                hideAll()
                try {
                    api.clearCrosshairPosition()
                } catch {
                    // chart may be mid-update
                }
                setCrosshairActive(false)
            }
            try {
                api.applyOptions({
                    crosshair: on
                        ? { mode: CrosshairMode.Hidden, vertLine: { visible: false }, horzLine: { visible: false } }
                        : {
                              mode: CrosshairMode.Magnet,
                              horzLine: { visible: false, labelVisible: false },
                              vertLine: { visible: true, labelVisible: false, style: LineStyle.LargeDashed, width: 1 }
                          }
                })
            } catch {
                // chart may be mid-update
            }
            report()
        }
        const pollAlertEdit = () => setAlertEdit(chart.isChartAlertEditActive())
        const pollId = window.setInterval(pollAlertEdit, 250)

        // ── lightweight-charts crosshair (single finger tracking mode) ──────
        const onCrosshairMove = (param: MouseEventParams<Time>) => {
            // While we own the touch (range / single fallback) the card is rendered from the touch itself.
            if (owningTouch()) return
            const active = !!param.point && !alertEdit
            if (active) renderSingle(hitFromParam(param))
            else hideAll()
            setCrosshairActive(active)
        }
        api.subscribeCrosshairMove(onCrosshairMove)

        // ── Touch handling on the chart element (capture phase, ahead of the library) ──
        const host = api.chartElement()
        const paneX = (t: Touch) => {
            const geo = g()
            const rootRect = root.getBoundingClientRect()
            return t.clientX - rootRect.left - geo.left
        }
        const updateRange = (ev: TouchEvent) => {
            currentGeometry = geometry()
            if (!currentGeometry) return
            if (!renderRange(paneX(ev.touches[0]), paneX(ev.touches[1]))) hideAll()
        }
        /**
         * Single-finger fallback after a range gesture: move the library's crosshair line to the
         * finger's bar and draw the card ourselves. `setCrosshairPosition` deliberately does not
         * fire `crosshairMove` (it positions with `skipEvent`), so we cannot rely on the event path.
         */
        const driveSingle = (touch: Touch) => {
            currentGeometry = geometry()
            const series = primarySeries()
            if (!currentGeometry || !series) {
                hideAll()
                return
            }
            const hit = barAtX(series, paneX(touch))
            const price = hit ? hit.bar.close ?? hit.bar.value : undefined
            if (!hit || price === undefined) {
                hideAll()
                return
            }
            try {
                api.setCrosshairPosition(price, hit.bar.time, series)
            } catch {
                // chart may be mid-update
            }
            lineL.style.display = 'none'
            lineR.style.display = 'none'
            cardL.style.display = 'none'
            cardR.style.display = 'none'
            cardM.style.display = 'none'
            renderSingle({ series, bar: hit.bar, logical: hit.logical, x: hit.x })
        }
        const enterRange = (ev: TouchEvent) => {
            if (range.mode === 'idle') {
                // Take the touch away from the library (which owns the long-press crosshair).
                resetLibraryTouch(range.firstTarget ?? ev.target ?? host, ev.touches)
            }
            range.mode = 'range'
            try {
                api.clearCrosshairPosition()
            } catch {
                // chart may be mid-update
            }
            hideAll()
            setCrosshairActive(true)
        }
        const onTouchStart = (ev: TouchEvent) => {
            if (alertEdit || replaying) return
            if (ev.touches.length === 1) {
                // A fresh single-finger gesture while we still think we own a touch means the
                // previous gesture's end was lost (view hidden, host swallowed it) — start clean.
                if (owningTouch()) reset()
                range.firstTarget = ev.target
                return
            }
            if (ev.touches.length < 2) return
            ev.stopPropagation()
            if (ev.cancelable) ev.preventDefault()
            if (range.mode !== 'range') enterRange(ev)
            updateRange(ev)
        }
        const onTouchMove = (ev: TouchEvent) => {
            if (!owningTouch() || replaying) return
            ev.stopPropagation()
            if (ev.cancelable) ev.preventDefault()
            if (range.mode === 'range' && ev.touches.length >= 2) updateRange(ev)
            else if (range.mode === 'single' && ev.touches.length === 1) driveSingle(ev.touches[0])
        }
        // We keep the touch until every finger lifts. Lifting one of two fingers falls back to the
        // single crosshair on the remaining finger; lifting the last one clears everything.
        const onTouchEnd = (ev: TouchEvent) => {
            if (!owningTouch() || replaying) return
            ev.stopPropagation()
            if (ev.type === 'touchcancel' || ev.touches.length === 0) {
                endRange()
            } else if (ev.touches.length === 1) {
                range.mode = 'single'
                hideAll()
                driveSingle(ev.touches[0])
            }
        }
        // Native hosts cancel the touch when a parent sheet takes over the gesture; lightweight-charts
        // only resets on `touchend`, so replay one or the next long-press is swallowed.
        const onTouchCancel = (ev: TouchEvent) => {
            if (replaying) return
            resetLibraryTouch(ev.target ?? host, ev.changedTouches)
            try {
                api.clearCrosshairPosition()
            } catch {
                // chart may be mid-update
            }
            hideAll()
            setCrosshairActive(false)
        }
        const captureNonPassive: AddEventListenerOptions = { capture: true, passive: false }
        host.addEventListener('touchstart', onTouchStart, captureNonPassive)
        host.addEventListener('touchmove', onTouchMove, captureNonPassive)
        host.addEventListener('touchend', onTouchEnd, true)
        host.addEventListener('touchcancel', onTouchEnd, true)
        host.addEventListener('touchcancel', onTouchCancel, true)

        // Hosts cache the WebView: dismissing the sheet hides the page without unmounting it, and a
        // gesture in flight at that moment never gets its touchend. Reset whenever the page goes
        // away or the host asks for it explicitly (`chartManager.resetInlineCrosshair()`).
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') reset()
        }
        document.addEventListener('visibilitychange', onVisibilityChange)
        window.addEventListener('pagehide', reset)
        window.addEventListener('blur', reset)
        window.addEventListener(INLINE_CROSSHAIR_RESET_EVENT, reset)

        setAlertEdit(chart.isChartAlertEditActive())
        report()

        return () => {
            window.clearInterval(pollId)
            try {
                api.unsubscribeCrosshairMove(onCrosshairMove)
            } catch {
                // chart already destroyed
            }
            host.removeEventListener('touchstart', onTouchStart, captureNonPassive)
            host.removeEventListener('touchmove', onTouchMove, captureNonPassive)
            host.removeEventListener('touchend', onTouchEnd, true)
            host.removeEventListener('touchcancel', onTouchEnd, true)
            host.removeEventListener('touchcancel', onTouchCancel, true)
            document.removeEventListener('visibilitychange', onVisibilityChange)
            window.removeEventListener('pagehide', reset)
            window.removeEventListener('blur', reset)
            window.removeEventListener(INLINE_CROSSHAIR_RESET_EVENT, reset)
            range.mode = 'idle'
            try {
                api.clearCrosshairPosition()
            } catch {
                // chart already destroyed
            }
            all.forEach((d) => d.remove())
            // Another view is taking over this (cached) WebView: make sure native is not left locked.
            if (crosshairActive || alertEdit) {
                crosshairActive = false
                alertEdit = false
                report()
            }
        }
    }, [chart])

    return (
        <div
            ref={rootRef}
            style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 20, overflow: 'hidden' }}
            aria-hidden
        />
    )
}

export default memo(InlineCrosshairOverlay)
